import React from "react";
import "../../OutboundCampaign/VoicemailRetrySettings.css";

export const MAX_SEQUENCE_STEPS = 10;

export const createEmptySequenceStep = () => ({
  workspace_id: "",
  days_until_send: "",
});

export const isSequenceStepPending = (step) => {
  const status = String(step?.status || "pending")
    .trim()
    .toLowerCase();
  return status === "pending";
};

export const hasNonPendingSequenceSteps = (sequences) =>
  Array.isArray(sequences) && sequences.some((step) => !isSequenceStepPending(step));

export const normalizeSequenceSteps = (sequences) => {
  if (!Array.isArray(sequences)) return [];
  return sequences.map((step) => ({
    id: step.id || step.sequence_id || undefined,
    workspace_id: String(step.workspace_id || ""),
    days_until_send:
      step.days_until_send === 0 || step.days_until_send
        ? String(step.days_until_send)
        : "",
    status: step.status || "pending",
    workspace_name: step.workspace_name || "",
  }));
};

const SequenceCampaignSection = ({
  enabled = false,
  sequences = [],
  workspaceOptions = [],
  errors = {},
  disabled = false,
  onToggle,
  onChange,
  onNotify,
  inputStyle = {},
}) => {
  const handleToggleChange = (checked) => {
    if (disabled) return;
    // Don't clear sequence steps that already progressed beyond pending.
    if (!checked && hasNonPendingSequenceSteps(sequences)) {
      if (typeof onNotify === "function") {
        onNotify(
          "Cannot turn off sequence campaign because one or more follow-up steps have already started or completed."
        );
      }
      return;
    }
    if (typeof onToggle === "function") {
      onToggle(checked);
    }
    if (checked && (!Array.isArray(sequences) || sequences.length === 0)) {
      if (typeof onChange === "function") {
        onChange([createEmptySequenceStep()]);
      }
    }
    if (!checked && typeof onChange === "function") {
      onChange([]);
    }
  };

  const updateStep = (index, key, value) => {
    if (disabled) return;
    const step = sequences[index];
    if (!isSequenceStepPending(step)) return;
    const next = sequences.map((item, i) =>
      i === index ? { ...item, [key]: value } : item
    );
    if (typeof onChange === "function") {
      onChange(next);
    }
  };

  const addStep = () => {
    if (disabled || sequences.length >= MAX_SEQUENCE_STEPS) return;
    if (typeof onChange === "function") {
      onChange([...sequences, createEmptySequenceStep()]);
    }
  };

  const removeStep = (index) => {
    if (disabled) return;
    const step = sequences[index];
    // Avoid remapping ids/statuses for steps already sent/failed.
    if (!isSequenceStepPending(step)) return;
    const next = sequences.filter((_, i) => i !== index);
    if (typeof onChange === "function") {
      onChange(next.length ? next : [createEmptySequenceStep()]);
    }
  };

  return (
    <div className="row mt-3" data-error-key="is_sequence_campaign">
      <div className="col-md-12">
        <div
          className="voicemail-retry-settings"
          style={disabled ? { opacity: 0.7 } : undefined}
        >
          <div className="form-group voicemail-retry-toggle">
            <div className="voicemail-retry-toggle-wrapper">
              <label
                className="voicemail-retry-toggle-label mb-0"
                htmlFor="sms-sequence-campaign-toggle"
              >
                Is this a sequence campaign?
              </label>

              <span
                className={
                  enabled
                    ? "kt-switch kt-switch-sm kt-switch--success chat-cus-check voicemail-retry-switch"
                    : "kt-switch kt-switch-sm kt-switch--error chat-cus-check voicemail-retry-switch"
                }
              >
                <label>
                  <input
                    type="checkbox"
                    id="sms-sequence-campaign-toggle"
                    name="is_sequence_campaign"
                    checked={!!enabled}
                    onChange={(e) => handleToggleChange(e.target.checked)}
                    disabled={disabled}
                  />
                  <span />
                </label>
              </span>
            </div>
          </div>

          <p className="rules-match-voicemail-desc">
            Send automated follow-up texts to the same contacts after defined
            intervals.
          </p>

          {enabled && (
            <div className="voicemail-retry-settings-container mt-3">
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#495057",
                  marginBottom: "12px",
                }}
              >
                Sequence follow-ups
                <span
                  style={{
                    display: "block",
                    fontWeight: 400,
                    fontSize: "13px",
                    color: "#6c757d",
                    marginTop: "4px",
                  }}
                >
                  Each step sends after the previous one completes, using the
                  selected workspace. Max {MAX_SEQUENCE_STEPS} sequences.
                </span>
              </div>

              {sequences.map((step, index) => {
                const workspaceError = errors[`sequences.${index}.workspace_id`];
                const daysError = errors[`sequences.${index}.days_until_send`];
                const stepLocked = disabled || !isSequenceStepPending(step);
                const canRemove =
                  sequences.length > 1 &&
                  !disabled &&
                  isSequenceStepPending(step);
                return (
                  <div
                    key={step.id || `sequence-step-${index}`}
                    data-error-key={`sequences.${index}.workspace_id`}
                    style={{
                      border: "1px solid #e1e5e9",
                      borderRadius: "8px",
                      padding: "14px",
                      marginBottom: "12px",
                      background: "#fff",
                    }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-between"
                      style={{ marginBottom: "10px" }}
                    >
                      <strong style={{ color: "#495057", fontSize: "14px" }}>
                        Sequence {index + 1}
                      </strong>
                      {canRemove && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => removeStep(index)}
                          title="Remove sequence"
                          aria-label={`Remove sequence ${index + 1}`}
                          style={{ color: "#dc3545", fontWeight: 600 }}
                        >
                          <i className="fa fa-trash" aria-hidden="true" />
                        </button>
                      )}
                    </div>

                    <div className="row">
                      <div className="col-md-7">
                        <div className="form-group" style={{ marginBottom: 10 }}>
                          <label className="pd10botbuilder">
                            Which workspace do you want to send next?
                          </label>
                          <select
                            name={`sequences.${index}.workspace_id`}
                            className={`form-control ${
                              workspaceError ? "is-invalid" : ""
                            }`}
                            value={step.workspace_id || ""}
                            onChange={(e) =>
                              updateStep(index, "workspace_id", e.target.value)
                            }
                            disabled={stepLocked}
                            style={{
                              border: workspaceError
                                ? "1px solid #dc3545"
                                : "1px solid #e1e5e9",
                              ...inputStyle,
                            }}
                          >
                            <option value="">Select workspace</option>
                            {workspaceOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.text}
                              </option>
                            ))}
                          </select>
                          {workspaceError && (
                            <div
                              className="error invalid-feedback d-block"
                              style={{
                                color: "#dc3545",
                                fontSize: "12px",
                                marginTop: "5px",
                              }}
                            >
                              {workspaceError}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-5">
                        <div className="form-group" style={{ marginBottom: 10 }}>
                          <label className="pd10botbuilder">
                            In how many days?
                          </label>
                          <input
                            type="number"
                            name={`sequences.${index}.days_until_send`}
                            min={1}
                            max={365}
                            className={`form-control ${
                              daysError ? "is-invalid" : ""
                            }`}
                            value={step.days_until_send}
                            onChange={(e) =>
                              updateStep(index, "days_until_send", e.target.value)
                            }
                            disabled={stepLocked}
                            placeholder="1–365"
                            style={{
                              border: daysError
                                ? "1px solid #dc3545"
                                : "1px solid #e1e5e9",
                              ...inputStyle,
                            }}
                          />
                          {daysError && (
                            <div
                              className="error invalid-feedback d-block"
                              style={{
                                color: "#dc3545",
                                fontSize: "12px",
                                marginTop: "5px",
                              }}
                            >
                              {daysError}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {errors.sequences && (
                <div
                  className="error"
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginBottom: "10px",
                  }}
                >
                  {errors.sequences}
                </div>
              )}

              <button
                type="button"
                className="btn add-link"
                onClick={addStep}
                disabled={disabled || sequences.length >= MAX_SEQUENCE_STEPS}
                style={{
                  fontSize: "14px",
                  cursor:
                    disabled || sequences.length >= MAX_SEQUENCE_STEPS
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    disabled || sequences.length >= MAX_SEQUENCE_STEPS ? 0.6 : 1,
                }}
              >
                <i className="la la-plus mr-1" />
                Add Another Sequence
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SequenceCampaignSection;
